import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { safeUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const FALLBACK_SPOTLIGHT_IMAGE = 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=800&h=1200&fit=crop&crop=face';

const HighlightsSlideshow = () => {
  const [clips, setClips] = useState([]);
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/highlights`)
      .then(r => r.json())
      .then(data => { if (data.clips?.length) setClips(data.clips); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (clips.length < 2) return;
    timerRef.current = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setIdx(i => (i + 1) % clips.length);
        setFading(false);
      }, 400);
    }, 3500);
    return () => clearInterval(timerRef.current);
  }, [clips]);

  const current = clips[idx];
  const thumbUrl = current?.thumbnail_url ? safeUrl(current.thumbnail_url) : null;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {thumbUrl ? (
        <img
          key={idx}
          src={thumbUrl}
          alt={current?.title || 'BGN Highlights'}
          className="editorial-card-image"
          style={{ transition: 'opacity 0.4s ease', opacity: fading ? 0 : 1 }}
        />
      ) : (
        <div style={{
          position: 'absolute', inset: 0, background: '#0a0a0a',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12,
        }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
          <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.55rem', color: 'rgba(255,255,255,0.15)', letterSpacing: '0.2em' }}>
            BGN HIGHLIGHTS
          </span>
        </div>
      )}
      {clips.length > 1 && (
        <div style={{
          position: 'absolute', bottom: '4.5rem', left: '50%', transform: 'translateX(-50%)',
          display: 'flex', gap: 5, zIndex: 30,
        }}>
          {clips.map((_, i) => (
            <button
              key={i}
              onClick={e => { e.preventDefault(); setIdx(i); clearInterval(timerRef.current); }}
              style={{
                width: i === idx ? 18 : 6, height: 6,
                borderRadius: 3, border: 'none', cursor: 'pointer',
                background: i === idx ? '#fff' : 'rgba(255,255,255,0.35)',
                transition: 'all 0.3s ease', padding: 0,
              }}
              aria-label={`Clip ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const EditorialHero = () => {
  const [spotlightImage, setSpotlightImage] = useState(FALLBACK_SPOTLIGHT_IMAGE);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/spotlight`)
      .then(r => r.json())
      .then(data => {
        const url = safeUrl(data.spotlight?.image_url);
        if (url) setSpotlightImage(url);
      })
      .catch(() => {});
  }, []);

  const sections = [
    {
      id: 'highlights',
      title: 'highlights',
      category: 'best clips',
      link: '/highlights',
      isHighlights: true,
    },
    {
      id: 'spotlight',
      title: 'spotlight',
      category: 'featured stories',
      image: spotlightImage,
      link: '#spotlight',
    },
    {
      id: 'health',
      title: 'health',
      category: 'wellness & gaming',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=1200&fit=crop',
      comingSoon: true,
    },
    {
      id: 'merch',
      title: 'merch',
      category: 'official store',
      image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=800&h=1200&fit=crop',
      comingSoon: true,
    },
    // tech panel hidden — kept for easy re-enable
    // {
    //   id: 'tech',
    //   title: 'tech',
    //   category: 'gaming gear',
    //   image: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800&h=1200&fit=crop',
    //   comingSoon: true,
    // }
  ];

  const handleSectionClick = (link) => {
    if (link.startsWith('#')) {
      const element = document.querySelector(link);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="section-hero">
      <div className="container">
        <div className="grid-4">
          {sections.map((section) => (
            <div key={section.id} className={`editorial-card group ${section.comingSoon ? 'cursor-default' : ''}`}>
              {section.isHighlights ? (
                <HighlightsSlideshow />
              ) : (
                <img
                  src={safeUrl(section.image) || ''}
                  alt={section.title}
                  className={`editorial-card-image ${section.comingSoon ? 'grayscale opacity-60' : ''}`}
                />
              )}
              <div className="editorial-card-overlay" />

              <div className="editorial-card-content">
                <div className="editorial-card-category">
                  BGN {section.category}
                </div>
                <h2 className="editorial-card-title">
                  BGN<br />{section.title}
                </h2>
                {section.comingSoon && (
                  <span className="inline-block mt-3 text-xs font-medium uppercase tracking-widest text-white/70 border border-white/30 px-2 py-1">
                    Coming Soon
                  </span>
                )}
              </div>

              {!section.comingSoon && (
                <Link
                  to={section.link.startsWith('#') ? '/' : section.link}
                  onClick={() => handleSectionClick(section.link)}
                  className="absolute inset-0 z-20"
                  aria-label={`Visit ${section.title} section`}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EditorialHero;
